import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { gqlMediaTypeCodeEnum } from '@minecraft.graphql-types';
import {
  SearchArtistMediaDoc,
  SearchArtistMediaIncludeType,
  MediaTypeCodeEnum,
  SearchArtistMediaIncludeActive,
} from '@minecraft.graphql-operations';
import { captureExceptionEvent } from '@blocs.observability';

type UseMediaLibraryParams = {
  artistId: number;
  mediaTypes?: MediaTypeCodeEnum[];
  searchArtistMediaIncludeActive?: SearchArtistMediaIncludeActive;
  filterLocked?: boolean;
};

export const useMediaLibrary = ({
  artistId,
  mediaTypes = [MediaTypeCodeEnum.Audio, MediaTypeCodeEnum.Photo, MediaTypeCodeEnum.Video],
  searchArtistMediaIncludeActive = SearchArtistMediaIncludeActive.Active,
  filterLocked = true,
}: UseMediaLibraryParams) => {
  const includeType = useMemo(() => {
    const types: SearchArtistMediaIncludeType[] = [];
    if (mediaTypes.includes(MediaTypeCodeEnum.Photo)) {
      types.push(SearchArtistMediaIncludeType.Photo);
    }
    if (mediaTypes.includes(MediaTypeCodeEnum.Video)) {
      types.push(SearchArtistMediaIncludeType.Video);
    }
    if (mediaTypes.includes(MediaTypeCodeEnum.Audio)) {
      types.push(SearchArtistMediaIncludeType.Audio);
    }
    return types;
  }, [mediaTypes]);

  const {
    data: artistMediaData,
    loading: artistMediaLoading,
    error: artistMediaError,
    refetch: refetchArtistMedia,
  } = useQuery(SearchArtistMediaDoc, {
    variables: {
      input: {
        query: {
          artistId,
          includeType,
          includeActive: [searchArtistMediaIncludeActive],
        },
        page: {
          pageNumber: 1,
          pageSize: 999,
        },
      },
    },
    skip: !artistId,
  });

  const refetchMediaLibraryMedia = useCallback(async () => {
    try {
      await refetchArtistMedia();
    } catch (e) {
      captureExceptionEvent(e);
    }
  }, [refetchArtistMedia]);

  return useMemo(() => {
    const mediaAssets = [...(artistMediaData?.searchArtistMedia?.objects ?? [])].filter((media) =>
      filterLocked ? !media.isLocked : true
    );

    const photoMediaLibrary = mediaAssets.filter((media) => media?.mediaType?.code === MediaTypeCodeEnum.Photo);
    const videoMediaLibrary = mediaAssets.filter((media) => media?.mediaType?.code === MediaTypeCodeEnum.Video);
    const audioMediaLibrary = mediaAssets.filter((media) => media?.mediaType?.code === MediaTypeCodeEnum.Audio);
    return {
      mediaLibraryLoading: artistMediaLoading,
      mediaLibraryErr: artistMediaError,
      mediaLibraryAssets: mediaAssets,
      mediaLibraryByType: {
        [gqlMediaTypeCodeEnum.PHOTO]: photoMediaLibrary,
        [gqlMediaTypeCodeEnum.VIDEO]: videoMediaLibrary,
        [gqlMediaTypeCodeEnum.AUDIO]: audioMediaLibrary,
      },
      photoMediaLibrary,
      videoMediaLibrary,
      audioMediaLibrary,
      refetchMediaLibraryMedia,
      artistMediaEntitlement: artistMediaData?.searchArtistMedia?.artist,
    };
  }, [artistMediaData, artistMediaError, artistMediaLoading, filterLocked, refetchMediaLibraryMedia]);
};

export type UseMediaLibraryValues = ReturnType<typeof useMediaLibrary>;
